import type { ReactNode } from 'react';
import { Icon } from './Icon';

/**
 * Previa de como a mensagem chega no aparelho (RNF15).
 *
 * `WhatsAppPreview` desenha a bolha verde-escura do WhatsApp com ate dois
 * botoes de resposta ("Tirar duvida", "Ir para o link"). `EmailPreview` mostra
 * assunto, remetente e corpo com o botao de acao. As variaveis
 * `{primeiro_nome}`, `{produto}`, `{link}` ficam destacadas em cor de marca para
 * quem escreve a copy ver o que sera substituido.
 *
 * Nenhuma das duas fala com a rede: sao apenas HTML e CSS.
 */

const VARIAVEL = /(\{[a-z_]+\}|\{\{\d+\}\})/g;

export function destacarVariaveis(texto: string): ReactNode[] {
  return texto.split(VARIAVEL).map((parte, i) =>
    VARIAVEL.test(parte) ? (
      <mark key={i} className="rounded bg-transparent px-0.5 font-semibold text-brand">
        {parte}
      </mark>
    ) : (
      <span key={i}>{parte}</span>
    ),
  );
}

export interface PreviewButton {
  readonly label: string;
  readonly kind: 'link' | 'reply';
}

export function WhatsAppPreview({
  from,
  body,
  buttons,
  footer,
  time = '10:32',
  className = '',
}: {
  /** Nome do numero que envia, como aparece no topo do chat. */
  readonly from: string;
  readonly body: string;
  readonly buttons?: readonly PreviewButton[];
  readonly footer?: string;
  readonly time?: string;
  readonly className?: string;
}): JSX.Element {
  return (
    <div className={`overflow-hidden rounded-2xl border border-line bg-[#0b141a] text-[#e9edef] shadow-soft ${className}`}>
      <div className="flex items-center gap-3 bg-[#1f2c34] px-4 py-2.5">
        <span aria-hidden="true" className="grid h-8 w-8 place-items-center rounded-full bg-[#00a884] text-white">
          <Icon name="whatsapp" size="sm" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{from}</p>
          <p className="text-[11px] text-[#8696a0]">conta comercial</p>
        </div>
      </div>
      <div className="px-4 py-5" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize: '14px 14px' }}>
        <div className="max-w-[88%] rounded-xl rounded-tl-sm bg-[#1f2c34] px-3 py-2 text-[13px] leading-relaxed shadow-soft">
          <p className="whitespace-pre-wrap">{destacarVariaveis(body)}</p>
          {footer === undefined ? null : <p className="mt-1.5 text-[11px] text-[#8696a0]">{footer}</p>}
          <p className="mt-1 text-right text-[10px] text-[#8696a0]">{time}</p>
        </div>
        {buttons !== undefined && buttons.length > 0 ? (
          <div className="mt-1 flex max-w-[88%] flex-col gap-1">
            {buttons.map((b) => (
              <div key={b.label} className="flex items-center justify-center gap-1.5 rounded-lg bg-[#1f2c34] px-3 py-2 text-[13px] font-medium text-[#53bdeb]">
                <Icon name={b.kind === 'link' ? 'externo' : 'resposta'} size="xs" />
                {b.label}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function EmailPreview({
  from,
  subject,
  preheader,
  body,
  cta,
  className = '',
}: {
  readonly from: string;
  readonly subject: string;
  readonly preheader?: string;
  readonly body: string;
  readonly cta?: string;
  readonly className?: string;
}): JSX.Element {
  return (
    <div className={`overflow-hidden rounded-2xl border border-line bg-elevated shadow-soft ${className}`}>
      <div className="border-b border-line px-4 py-3">
        <p className="text-xs text-ink-subtle">
          De <span className="text-ink">{from}</span>
        </p>
        <p className="mt-0.5 text-sm font-semibold text-ink">{destacarVariaveis(subject)}</p>
        {preheader === undefined ? null : <p className="text-xs text-ink-subtle">{destacarVariaveis(preheader)}</p>}
      </div>
      <div className="px-4 py-4 text-sm leading-relaxed text-ink-muted">
        <p className="whitespace-pre-wrap">{destacarVariaveis(body)}</p>
        {cta === undefined ? null : (
          <p className="mt-4 text-center">
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white">
              <Icon name="externo" size="xs" />
              {cta}
            </span>
          </p>
        )}
      </div>
    </div>
  );
}
